import { CssBaseline, ThemeProvider, useColorScheme } from '@mui/material'
import { ColorScheme, DarkFlavor } from '@simplisafe/compass-web'
import { FC, StrictMode, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom/client'
import { App } from './App'
import { StyledSnackbarProvider } from './components/StyledSnackbarProvider'
import { theme } from './styles/theme'
import { bool } from './utils/utils'
import { useSetAtom } from 'jotai'
import { colorModeContainerAtom } from './atoms'
import { initFaro } from './faro/faroUtils'
import { initOpenTelemetry } from './telemetry/initOpenTelemetry'

const isDexDevEnv = dex.config.name === 'dev'

if (!isDexDevEnv) {
  initOpenTelemetry()
}

const COLOR_MODE_LOCAL_STORAGE_KEY = 'ss-color-mode' as const

if (dex.config.faro?.enabled) {
  initFaro()
}

const rootElement = document.getElementById('root')

if (rootElement === null) {
  throw new Error('The application could not start because the root element was not found.')
}

async function startMockServiceWorker(): Promise<void> {
  if (bool(import.meta.env.VITE_ENABLE_AUTO_MOCK) || bool(import.meta.env.VITE_ENABLE_CUSTOM_MSW_HANDLERS)) {
    const { getWorker } = await import('./msw/setup/setupForBrowser')
    const worker = getWorker()
    await worker.start()
  }
}

const existingColorMode = localStorage.getItem(COLOR_MODE_LOCAL_STORAGE_KEY)
// TODO: Re-enable this feature once the dark mode color palette is updated
/*if (!existingColorMode || existingColorMode === 'system') {
  const userPreferredColorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  localStorage.setItem(COLOR_MODE_LOCAL_STORAGE_KEY, userPreferredColorScheme)
}*/

// TODO: Remove this once the dark mode color palette is updated and the toggle color mode button is re-enabled
if (existingColorMode !== 'light') {
  localStorage.setItem(COLOR_MODE_LOCAL_STORAGE_KEY, 'light')
}

function mountApp(rootElement: HTMLElement): void {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <ThemeProvider theme={theme} modeStorageKey={COLOR_MODE_LOCAL_STORAGE_KEY} noSsr>
        <AppRoot />
      </ThemeProvider>
    </StrictMode>
  )
}

// userColorScheme has to be called from inside ThemeProvider which is why we split it out.
// biome-ignore lint/style/useComponentExportOnlyModules: there is no reason to export this
const AppRoot: FC = () => {
  const { mode, setMode } = useColorScheme()
  const colorModeContainerRef = useRef<HTMLDivElement>(null)
  const setColorModeContainerAtom = useSetAtom(colorModeContainerAtom)

  useEffect(() => {
    if (colorModeContainerRef.current !== null) {
      setColorModeContainerAtom(colorModeContainerRef.current)
    }
  }, [setColorModeContainerAtom])

  // TODO: Remove this once the dark mode color palette is updated and the toggle color mode button is re-enabled.
  // MUI's CssVarsProvider syncs mode across tabs via the localStorage "storage" event, so a user can flip this
  // app into dark mode by editing localStorage directly (or from another open tab) without reloading the page.
  // The reset in the script above only covers page load, so this effect corrects it live as well.
  useEffect(() => {
    if (mode !== 'light') {
      setMode('light')
    }
  }, [mode, setMode])

  return (
    <ColorScheme id="color-mode-container" modeOverride={mode} darkFlavor={DarkFlavor.MIDNIGHT} ref={colorModeContainerRef}>
      <CssBaseline />
      <StyledSnackbarProvider>
        <App />
      </StyledSnackbarProvider>
    </ColorScheme>
  )
}

startMockServiceWorker().then(() => {
  mountApp(rootElement)
})
